import { useEffect } from "react";

/**
 * Per-page SEO metadata. Updates the document `<title>`, `<meta name="description">`,
 * Open Graph, Twitter card, canonical URL, and (optionally) a schema.org JSON-LD
 * `<script>` block on mount.
 *
 * The marketing SPA is rendered to static HTML at build time by the post-build
 * Playwright pre-renderer (frontend/apps/marketing/scripts/prerender.mjs), so
 * everything this hook sets is captured into the per-route index.html that
 * Spring Boot serves on first request. Crawlers + LLMs that don't execute JS
 * still see the right title, description, OG tags, and structured data.
 *
 * Usage at the top of any page component:
 *
 *     useSEO({
 *       title: "For Humans — Get paid for verified work",
 *       description: "Sign up. Verify. Pick up tasks. Get paid per verified label.",
 *       path: "/for-humans",
 *       jsonLd: { "@context": "https://schema.org", "@type": "WebPage", ... },
 *     });
 *
 * `path` is the URL path without origin — the hook prepends `SITE_URL` to
 * build the canonical + og:url. Defaults to `/` if omitted.
 *
 * `jsonLd` is an arbitrary schema.org JSON-LD object (or an array of objects).
 * It's serialized into a `<script type="application/ld+json">` tagged with
 * `data-seo-jsonld` so the hook can replace it on route change without
 * leaving stale entries behind.
 */
export interface SEO {
  /** Page title — shown in the browser tab. The hook appends "| Humaniti"
   *  unless the title already contains "Humaniti". */
  title: string;
  /** 1-2 sentence description for crawlers + social-card previews. */
  description: string;
  /** Path on the site (e.g. "/for-humans"). Becomes canonical + og:url. */
  path?: string;
  /** Optional override for og:image. Defaults to the site-wide social card. */
  ogImage?: string;
  /** Optional og:type. Defaults to "website"; use "article" for Field notes posts. */
  ogType?: "website" | "article";
  /** Optional schema.org JSON-LD (single object or array). Replaces any
   *  previously-set page-level JSON-LD on route change. */
  jsonLd?: object | object[];
}

const SITE_URL = "https://humaniti.io";
// Single shared social card. 1200x630 PNG generated at build time from the
// SVG at frontend/apps/marketing/scripts/og-card.svg (see Dockerfile).
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-card.png`;

/** Set or create a `<meta>` tag with the given key attribute (`name` or `property`). */
const setMeta = (keyAttr: "name" | "property", key: string, content: string): void => {
  const selector = `meta[${keyAttr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(keyAttr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

/** Set or create the `<link rel="canonical">` tag. */
const setCanonical = (href: string): void => {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Replace any page-level JSON-LD with the given payload (or remove it if
 * `data` is undefined). The script is tagged `data-seo-jsonld` so we can
 * safely identify and replace it without disturbing any other JSON-LD that
 * the index.html might ship with statically.
 */
const setJsonLd = (data: object | object[] | undefined): void => {
  const existing = document.head.querySelector<HTMLScriptElement>(
    'script[type="application/ld+json"][data-seo-jsonld]'
  );
  if (!data) {
    existing?.remove();
    return;
  }
  const el = existing ?? document.createElement("script");
  el.type = "application/ld+json";
  el.setAttribute("data-seo-jsonld", "");
  el.textContent = JSON.stringify(data);
  if (!existing) document.head.appendChild(el);
};

const decorateTitle = (title: string): string =>
  title.toLowerCase().includes("humaniti") ? title : `${title} | Humaniti`;

export const useSEO = ({
  title,
  description,
  path = "/",
  ogImage,
  ogType = "website",
  jsonLd,
}: SEO): void => {
  useEffect(() => {
    const fullTitle = decorateTitle(title);
    const url = `${SITE_URL}${path}`;
    const image = ogImage ?? DEFAULT_OG_IMAGE;

    document.title = fullTitle;
    setMeta("name", "description", description);
    setCanonical(url);

    setMeta("property", "og:type", ogType);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", image);

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    setJsonLd(jsonLd);
  }, [title, description, path, ogImage, ogType, jsonLd]);
};

/**
 * Component wrapper around `useSEO` so pages whose root is `() => (...)`
 * (single-expression arrow-return) don't need to be restructured into a
 * statement body just to call the hook. Drop `<SEO ... />` as the first
 * child of the page's root fragment:
 *
 *     export const FooPage = () => (
 *       <>
 *         <SEO title="Foo" description="..." path="/foo" />
 *         <Section>...</Section>
 *       </>
 *     );
 *
 * Renders nothing — its only effect is the hook side-effect.
 */
export const SEO = (props: SEO): null => {
  useSEO(props);
  return null;
};

// --------------------------------------------------------------------------
// Schema.org helpers — small typed factories so pages don't have to
// re-derive the same boilerplate. Consumers pass these into useSEO via the
// `jsonLd` field.
// --------------------------------------------------------------------------

/**
 * `Organization` schema describing Humaniti the company / product. Used on
 * the home page (and is fine to include on any page — search engines and
 * LLMs aggregate Organization signals across pages).
 */
export const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Humaniti",
  alternateName: "Humaniti.io",
  url: SITE_URL,
  logo: `${SITE_URL}/brand/humaniti-glyph.svg`,
  description:
    "A network for verified human-grade datasets. Builders publish labeling tasks, Humans complete them, peer-consensus verifies every label. Pay per verified label, not per hour.",
  slogan: "Datasets, by humans.",
  foundingDate: "2026",
  sameAs: [
    // Add real profile URLs when they exist (X, LinkedIn, GitHub org). Keep
    // an empty list rather than dead-link placeholders — sameAs with bad
    // URLs harms LLM trust more than missing it does.
  ],
} as const;

/**
 * Site-wide `WebSite` entity. Used on the home page; helps search engines
 * surface the search box for "humaniti.io" — also signals the canonical
 * site name to LLMs.
 */
export const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Humaniti",
  url: SITE_URL,
  description:
    "A network for verified human-grade datasets. Verified Humans, Verified work, Direct pay.",
  inLanguage: "en-US",
} as const;

/**
 * Build a `FAQPage` JSON-LD from a list of question/answer pairs. LLMs
 * disproportionately quote FAQPage content because the structure makes
 * the question→answer mapping unambiguous.
 */
export const faqPageJsonLd = (faqs: { question: string; answer: string }[]): object => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
});

/**
 * Build an `Article` JSON-LD for a Field notes post. Date strings should be
 * ISO-8601 (the MDX frontmatter already stores them that way).
 */
export const articleJsonLd = ({
  headline,
  description,
  url,
  datePublished,
  byline,
}: {
  headline: string;
  description: string;
  url: string;
  datePublished: string;
  byline: string;
}): object => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline,
  description,
  datePublished,
  author: { "@type": "Organization", name: byline },
  publisher: {
    "@type": "Organization",
    name: "Humaniti",
    logo: { "@type": "ImageObject", url: `${SITE_URL}/brand/humaniti-glyph.svg` },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": url },
});

/**
 * Build a generic `WebPage` JSON-LD that points back to the site Organization.
 * Use for pages where neither FAQPage nor Article fits.
 */
export const webPageJsonLd = ({
  name,
  description,
  url,
}: {
  name: string;
  description: string;
  url: string;
}): object => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name,
  description,
  url,
  isPartOf: { "@type": "WebSite", name: "Humaniti", url: SITE_URL },
  about: { "@type": "Organization", name: "Humaniti", url: SITE_URL },
});
